import React from 'react';
import { Modal, Pressable, Text, View, StyleSheet, ScrollView } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Paprika, PaprikaEntry } from '../../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { formatDayMonth } from '../../../../utils/dateLocale';
import {
  leftoverRowMeta,
  FRIDGE_LEFTOVER_DAYS,
  type LeftoverCandidate,
} from '../leftovers';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  fridge: LeftoverCandidate[];
  freezer: LeftoverCandidate[];
  dayLabel: string;
  /** Today as YYYY-MM-DD, injected so the expiry-warning rule stays testable. */
  today: string;
  resolve: (id: string) => RecipeIndexEntry | undefined;
  onPick: (c: LeftoverCandidate) => void;
  onCancel: () => void;
}

export function LeftoverPickerModal({
  fridge, freezer, dayLabel, today, resolve, onPick, onCancel,
}: Props) {
  const { t, i18n } = useTranslation();
  const isEmpty = fridge.length === 0 && freezer.length === 0;
  const fmt = (iso: string) => formatDayMonth(iso, i18n.language, 'short');

  const renderRow = (c: LeftoverCandidate) => {
    const meta = leftoverRowMeta(c, t, (id) => resolve(id)?.title, fmt, today);
    const image = c.kind === 'fridge' ? resolve(c.refRecipeId)?.image : undefined;
    const toneStyle =
      meta.tone === 'warn' ? styles.pillWarn
      : meta.tone === 'muted' ? styles.pillMuted
      : styles.pillCount;
    const toneText =
      meta.tone === 'warn' ? styles.pillWarnText
      : meta.tone === 'muted' ? styles.pillMutedText
      : styles.pillCountText;

    return (
      <Pressable
        key={c.key}
        testID={`leftover-row-${c.key}`}
        style={[styles.row, meta.disabled && styles.rowDisabled]}
        disabled={meta.disabled}
        onPress={() => onPick(c)}
      >
        <View style={styles.photoWrap}>
          <RemoteImage
            uri={image}
            variant="thumb"
            style={styles.photo}
            resizeMode="cover"
            fallback={<View style={[styles.photo, styles.photoPlaceholder]}>
                <MaterialCommunityIcons
                  name={c.kind === 'freezer' ? 'snowflake' : 'recycle'}
                  size={18}
                  color={PaprikaEntry.leftover.color}
                />
              </View>}
          />
        </View>

        <View style={styles.rowInfo}>
          <Text style={styles.rowTitle} numberOfLines={1}>{meta.title}</Text>
          <Text style={styles.rowSub} numberOfLines={1}>{meta.sub}</Text>
        </View>

        {meta.badge !== undefined && (
          <View style={[styles.pill, toneStyle]}>
            <Text style={[styles.pillText, toneText]}>{meta.badge}</Text>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <Modal transparent visible onRequestClose={onCancel}>
      <Pressable style={styles.scrim} onPress={onCancel} />
      <View style={styles.container}>
        <View style={[styles.modal, { backgroundColor: Paprika.surfaceLowest }]}>
          <View style={[styles.header, { backgroundColor: PaprikaEntry.leftover.tint }]}>
            <View style={styles.iconSquare}>
              <MaterialCommunityIcons name="recycle" size={20} color={PaprikaEntry.leftover.color} />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.title}>{t('entry.leftovers')}</Text>
              <Text style={styles.subtitle}>
                {t('thisWeek.leftoverPickerSubtitle', { day: dayLabel })}
              </Text>
            </View>
            <Pressable style={styles.closeBtn} onPress={onCancel} accessibilityLabel={t('common.close')}>
              <MaterialCommunityIcons name="close" size={18} color={Paprika.textSecondary} />
            </Pressable>
          </View>

          {isEmpty ? (
            <View style={styles.emptyState} testID="leftover-empty">
              <Text style={styles.emptyTitle}>{t('thisWeek.leftoverNoneTitle')}</Text>
              <Text style={styles.emptyText}>{t('thisWeek.leftoverNoneBody')}</Text>
            </View>
          ) : (
            <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
              {fridge.length > 0 && (
                <>
                  <Text style={styles.sectionLabel}>
                    {t('thisWeek.leftoverFridgeSection', { days: FRIDGE_LEFTOVER_DAYS })}
                  </Text>
                  {fridge.map(renderRow)}
                </>
              )}
              {freezer.length > 0 && (
                <>
                  <Text style={[styles.sectionLabel, styles.sectionLabelFreezer]}>
                    {t('thisWeek.leftoverFreezerSection')}
                  </Text>
                  {freezer.map(renderRow)}
                </>
              )}
            </ScrollView>
          )}

          <View style={styles.footer}>
            <Pressable style={styles.ghostBtn} onPress={onCancel}>
              <Text style={styles.ghostBtnText}>{t('common.close')}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute' as any,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(27, 28, 26, 0.34)',
  },
  container: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  modal: {
    width: 430,
    borderRadius: 20,
    shadowColor: 'rgba(82, 68, 60, 0.14)',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 16,
    overflow: 'hidden',
    maxHeight: '78%',
    flexDirection: 'column',
  },
  header: { flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 },
  iconSquare: { width: 44, height: 44, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  headerText: { flex: 1, gap: 4 },
  title: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 20, lineHeight: 26,
    color: Paprika.textPrimary,
  },
  subtitle: { fontFamily: 'Inter-Regular', fontSize: 13, color: Paprika.textSecondary },
  closeBtn: { width: 32, height: 32, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  list: { flex: 1 },
  sectionLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: PaprikaEntry.leftover.color,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 4,
  },
  sectionLabelFreezer: { color: '#3a6a8a' },
  emptyState: {
    flex: 1, justifyContent: 'center', alignItems: 'center',
    paddingHorizontal: 24, paddingVertical: 36, gap: 6,
  },
  emptyTitle: {
    fontFamily: 'Inter-Medium', fontSize: 14,
    color: Paprika.textPrimary, textAlign: 'center',
  },
  emptyText: {
    fontFamily: 'Inter-Regular', fontSize: 13, lineHeight: 19,
    textAlign: 'center', color: Paprika.textSecondary,
  },
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 10,
    gap: 12, marginVertical: 2,
  },
  rowDisabled: { opacity: 0.45 },
  photoWrap: { width: 48, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0 },
  photo: { width: 48, height: 48, justifyContent: 'center', alignItems: 'center' },
  photoPlaceholder: { backgroundColor: PaprikaEntry.leftover.tint },
  rowInfo: { flex: 1, gap: 2 },
  rowTitle: {
    fontFamily: 'Inter-Medium', fontSize: 13.5, lineHeight: 18,
    color: Paprika.textPrimary,
  },
  rowSub: { fontFamily: 'Inter-Regular', fontSize: 12, color: Paprika.textSecondary },
  pill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14, flexShrink: 0,
  },
  pillCount: { backgroundColor: PaprikaEntry.leftover.tint },
  pillCountText: { color: PaprikaEntry.leftover.color },
  pillWarn: { backgroundColor: Paprika.accentLight },
  pillWarnText: { color: '#9a5730' },
  pillMuted: { backgroundColor: Paprika.surfaceHighest },
  pillMutedText: { color: Paprika.textMuted },
  pillText: { fontFamily: 'Inter-SemiBold', fontSize: 11 },
  footer: { flexDirection: 'row', gap: 12, padding: 16 },
  ghostBtn: {
    flex: 1, paddingVertical: 11, paddingHorizontal: 16, borderRadius: 24,
    borderWidth: 1.5, borderColor: 'rgba(215, 194, 184, 0.4)',
    justifyContent: 'center', alignItems: 'center',
  },
  ghostBtnText: { fontFamily: 'Inter-Medium', fontSize: 14, color: Paprika.textSecondary },
});
